'use client';

import { useTranslations } from 'next-intl';
import { useState, useEffect, useRef } from 'react';
import { CustomImage as Image } from 'components/shared';
import {
  AirRingColors,
  getAvailableAirRingColors,
  getRingAirColorNameImages,
} from 'content/products/ring';
import styles from './RealLifeImagesModalContent.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

export const RealLifeImagesModalContent = ({
  className,
  selectedColor,
}: {
  className?: string;
  selectedColor: AirRingColors;
}) => {
  const t = useTranslations('ring');
  const tCommon = useTranslations('common');
  const RingAirColorNameImages = getRingAirColorNameImages(t);
  const availableColors = getAvailableAirRingColors();
  const [activeTab, setActiveTab] = useState<AirRingColors>(
    selectedColor && availableColors.includes(selectedColor)
      ? selectedColor
      : (availableColors[0] ?? AirRingColors.RAW_TITANIUM),
  );
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const idx = availableColors.indexOf(activeTab);
    if (btnRefs.current[idx]) {
      btnRefs.current[idx]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activeTab]);

  const getImageDir = (color: AirRingColors) => {
    switch (color) {
      case AirRingColors.RAW_TITANIUM:
        return 'rt';
      case AirRingColors.ASTER_BLACK:
        return 'ab';
      case AirRingColors.BIONIC_GOLD:
        return 'bg';
      case AirRingColors.SPACE_SILVER:
        return 'ss';
      case AirRingColors.MATTE_GREY:
        return 'mg';
      case AirRingColors.ROSE_GOLD:
        return 'rg-1';
      default:
        return 'rt';
    }
  };

  const extension = activeTab === AirRingColors.ROSE_GOLD ? 'png' : 'JPG';

  return (
    <div className={cx(styles.root, className)}>
      <h2>{tCommon('realLifeImagesModalContent.heading.realLifeImages')}</h2>
      <div className={cx(styles.btnContainerWrapper, 'u-hide-scrollbar')}>
        <div className={styles.btnContainer}>
          {availableColors.map((element, index) => {
            return (
              <button
                key={index}
                ref={(el) => {
                  btnRefs.current[index] = el;
                }}
                onClick={() => setActiveTab(element)}
                className={activeTab === element ? 'active' : undefined}
                type="button"
                aria-label={RingAirColorNameImages[element].name as string}
                aria-pressed={activeTab === element}
              >
                {RingAirColorNameImages[element].name}
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.imgContainer}>
        <Image
          src={`web_v2/ring-lifestyle-images/${getImageDir(
            activeTab,
          )}/1.${extension}/w_1080`}
          alt={RingAirColorNameImages[activeTab].name as string}
          width="400"
          height="400"
        />
        <Image
          src={`web_v2/ring-lifestyle-images/${getImageDir(
            activeTab,
          )}/2.${extension}/w_1080`}
          alt={RingAirColorNameImages[activeTab].name as string}
          width="400"
          height="400"
        />
        <Image
          src={`web_v2/ring-lifestyle-images/${getImageDir(
            activeTab,
          )}/3.${extension}/w_1080`}
          alt={RingAirColorNameImages[activeTab].name as string}
          width="400"
          height="400"
          className="full"
        />
        <Image
          src={`web_v2/ring-lifestyle-images/${getImageDir(
            activeTab,
          )}/4.${extension}/w_1080`}
          alt={RingAirColorNameImages[activeTab].name as string}
          width="400"
          height="400"
        />
        <Image
          src={`web_v2/ring-lifestyle-images/${getImageDir(
            activeTab,
          )}/5.${extension}/w_1080`}
          alt={RingAirColorNameImages[activeTab].name as string}
          width="400"
          height="400"
        />
        {![
          AirRingColors.MATTE_GREY,
          AirRingColors.ASTER_BLACK,
          AirRingColors.ROSE_GOLD,
        ].includes(activeTab) ? (
          <Image
            src={`web_v2/ring-lifestyle-images/${getImageDir(
              activeTab,
            )}/6.${extension}/w_1080`}
            alt={RingAirColorNameImages[activeTab].name as string}
            width="400"
            height="400"
            className="full"
          />
        ) : null}
      </div>
    </div>
  );
};
